// The yellow ring under the hazard a details panel is about, while someone
// steps through a card's hazards from "warnings on route" (PAGER-SPEC.md):
// the map eases to each one, and the ring says which marker the panel shows.
// Drawn just below the hazard markers (same layer category, a lower z), so
// the marker sits on top of it; hazard icons are centre-anchored, so the ring
// is centred on the hazard's own coordinate.
//
// The hazard's own marker is drawn here too, from the ring's point, just over
// the tiles' markers: with the Hazards switch off (or below the markers' zoom)
// the tiles draw nothing, which left an empty ring. Twins: Android/iOS
// HazardSelectionRingPlan.

import {
  HAZARD_ICON_ANCHOR,
  HAZARD_ICON_SIZE,
  HAZARD_LAYER_CATEGORY,
  HAZARD_LAYER_Z,
  HAZARD_PROP_ICON,
} from './hazardLayerSpec';
import { addBundledHazardImage, MissingImageMap } from './hazardMissingImage';
import { hazardSpriteName, isHazardSpriteName } from './hazardSprite';

export const HAZARD_RING_SOURCE_ID = 'hazard-selection-ring-source';
export const HAZARD_RING_LAYER_ID = 'hazard-selection-ring-layer';
export const HAZARD_RING_MARKER_LAYER_ID = 'hazard-selection-marker-layer';
/** Just under the markers (HAZARD_LAYER_Z) in their category. */
export const HAZARD_RING_Z = HAZARD_LAYER_Z - 0.5;
/** The ring's own marker: just over the tiles' markers, covering their copy of it exactly. */
export const HAZARD_RING_MARKER_Z = HAZARD_LAYER_Z + 0.5;

export const HAZARD_RING_PAINT = {
  'circle-radius': 22,
  'circle-color': '#FDDF00',
  'circle-opacity': 0.28,
  'circle-stroke-color': '#FDDF00',
  'circle-stroke-width': 3,
  'circle-stroke-opacity': 1,
};

export interface RingMap {
  getSource(id: string): any;
  getLayer(id: string): any;
  on(type: string, handler: (...args: any[]) => void): void;
  off(type: string, handler: (...args: any[]) => void): void;
}

export interface RingRegistry {
  addLayer(layer: any, category: any, opts?: { z?: number; source?: { id: string; spec: any; replace?: boolean } }): void;
  removeLayer(id: string, removeOrphanSource?: boolean): void;
}

export interface RingDeps {
  getMap: () => RingMap | null | undefined;
  getRegistry: () => RingRegistry | null | undefined;
  /** A style without the HiiKER sprite asked for an image: add the bundled hazard icon. */
  addMissingImage?: (map: RingMap, id: unknown) => void;
}

/**
 * The marker the ring draws for what was shown: a row's sprite icon, or the
 * alert's hazard's (`tree_blocked`). Null — just the ring — for a type this
 * bundle can't name.
 */
export function ringIcon(target: any): string | null {
  if (isHazardSpriteName(target?.iconName)) return target.iconName;
  const hazard = target?.hazard ?? target?.alert?.hazard;
  return hazard ? hazardSpriteName(hazard.hazardType, hazard.passability) : null;
}

export function ringFeatureCollection(point: { longitude: number; latitude: number } | null, icon: string | null = null) {
  return {
    type: 'FeatureCollection',
    features: point
      ? [{
        type: 'Feature',
        properties: icon ? { [HAZARD_PROP_ICON]: icon } : {},
        geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
      }]
      : [],
  };
}

export function ringLayerSpec() {
  return { id: HAZARD_RING_LAYER_ID, type: 'circle', source: HAZARD_RING_SOURCE_ID, paint: { ...HAZARD_RING_PAINT } };
}

/** The hazard's own marker on the ring's point: the tiles' marker look, at any zoom. */
export function ringMarkerLayerSpec() {
  return {
    id: HAZARD_RING_MARKER_LAYER_ID,
    type: 'symbol',
    source: HAZARD_RING_SOURCE_ID,
    layout: {
      'icon-image': ['get', HAZARD_PROP_ICON],
      'icon-size': HAZARD_ICON_SIZE,
      'icon-anchor': HAZARD_ICON_ANCHOR,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  };
}

function usableMap(candidate: unknown): RingMap | null {
  const map = candidate as any;
  return map && typeof map.getSource === 'function' && typeof map.on === 'function' ? (map as RingMap) : null;
}

export class HazardSelectionRing {
  private point: { longitude: number; latitude: number } | null = null;
  private icon: string | null = null;
  private boundMap: RingMap | null = null;

  constructor(private readonly deps: RingDeps) {}

  get shown(): { longitude: number; latitude: number } | null {
    return this.point;
  }

  /** The marker drawn on the ring (a sprite name), or null for just the ring. */
  get shownIcon(): string | null {
    return this.point ? this.icon : null;
  }

  /**
   * The ring on this hazard (moved there if it was on another), with its marker
   * on top: a route-alert row or alert carries the icon ({@link ringIcon}).
   */
  show(point: { longitude: number; latitude: number }): void {
    if (!Number.isFinite(point?.longitude) || !Number.isFinite(point?.latitude)) {
      this.clear();
      return;
    }
    this.point = { longitude: point.longitude, latitude: point.latitude };
    this.icon = ringIcon(point);
    this.sync();
  }

  /** No ring. */
  clear(): void {
    this.point = null;
    this.icon = null;
    const map = usableMap(this.deps.getMap());
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;
    try {
      if (map.getLayer(HAZARD_RING_MARKER_LAYER_ID)) registry.removeLayer(HAZARD_RING_MARKER_LAYER_ID, false);
      if (map.getLayer(HAZARD_RING_LAYER_ID)) registry.removeLayer(HAZARD_RING_LAYER_ID, true);
    } catch (error) {
      console.warn('[trail_hazards] could not remove the selection ring', error);
    }
    this.unbind();
  }

  dispose(): void {
    this.clear();
  }

  /** Draws the ring where it should be. Idempotent; a style switch calls it again. */
  readonly sync = (): void => {
    if (!this.point) return;
    const map = usableMap(this.deps.getMap());
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;
    const data = ringFeatureCollection(this.point, this.icon);
    try {
      const source = map.getSource(HAZARD_RING_SOURCE_ID);
      if (source && typeof source.setData === 'function') source.setData(data);
      if (!map.getLayer(HAZARD_RING_LAYER_ID)) {
        // The marker reads the ring's source: off first, so the source can be replaced under it.
        if (map.getLayer(HAZARD_RING_MARKER_LAYER_ID)) registry.removeLayer(HAZARD_RING_MARKER_LAYER_ID, false);
        registry.addLayer(ringLayerSpec(), HAZARD_LAYER_CATEGORY, {
          z: HAZARD_RING_Z,
          source: { id: HAZARD_RING_SOURCE_ID, spec: { type: 'geojson', data }, replace: true },
        });
      }
      if (!map.getLayer(HAZARD_RING_MARKER_LAYER_ID)) {
        registry.addLayer(ringMarkerLayerSpec(), HAZARD_LAYER_CATEGORY, { z: HAZARD_RING_MARKER_Z });
      }
      this.bind(map);
    } catch (error) {
      // "Style is not done loading": the next style.load draws it.
      console.warn('[trail_hazards] could not draw the selection ring yet', error);
      this.bind(map);
    }
  };

  private bind(map: RingMap): void {
    if (this.boundMap === map) return;
    this.unbind();
    map.on('style.load', this.sync);
    // The hazard layer's own handler isn't there with the Hazards switch off.
    map.on('styleimagemissing', this.onImageMissing);
    this.boundMap = map;
  }

  private readonly onImageMissing = (event: any): void => {
    const map = this.boundMap;
    if (!map) return;
    const add = this.deps.addMissingImage
      ?? ((target: RingMap, id: unknown) => { addBundledHazardImage(target as unknown as MissingImageMap, id); });
    add(map, event?.id);
  };

  private unbind(): void {
    const map = this.boundMap;
    this.boundMap = null;
    if (!map) return;
    try {
      map.off('style.load', this.sync);
      map.off('styleimagemissing', this.onImageMissing);
    } catch {
      // A removed map.
    }
  }
}
